import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Heading from '../../../generic-components/Heading';
import {
  getAnalyticsFlowName,
  isRequireDataForVendorValid,
  MoveMoneySteps,
  MoveMoneyTypes,
  vendorPickerContentMap,
} from '../consts';
import { ReactComponent as RightArrowIcon } from '../../../assets/icons/svg/shared/ic-shared-right-arrow.svg';
import { ReactComponent as AddVendorIcon } from '../../../assets/icons/svg/shared/ic-shared-add-account.svg';
import { VIEWS_MAP } from '../../../router/constants';
import { useStore } from '../../../store';
import { ISupplierModel } from '../../Supplier/types';
import { LiliSearchBar } from '../../../DesignSystem/LiliSearchBar/LiliSearchBar';
import { NavigationHeader } from '../components/NavigationHeader';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { AddNewVendor } from '../components/vendor-picker/AddNewVendor';
import { VendorsList } from '../components/vendor-picker/VendorsList';
import { isBusinessOrRecipientNameValid } from '../utils';

export const VendorPicker = () => {
  const { moveType } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const setMoveMoneyData = useStore((state) => state.setMoveMoneyData);
  const resetMoveMoneyData = useStore((state) => state.resetMoveMoneyData);

  const [filter, setFilter] = useState<string>('');

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleBackButton = () => {
    if (moveMoneyData.isEditMode) {
      setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.REVIEW });
    } else if (moveType === MoveMoneyTypes.INTERNATIONAL_WIRE_OUT) {
      if (moveMoneyData.introRequired) {
        setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.INTRO });
      } else {
        navigate(`/${VIEWS_MAP.DASHBOARD}`);
      }
    } else {
      setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.INTRO });
    }
  };

  const handleAddNewVendor = (preFilledName?: string) => {
    if (preFilledName) {
      trackMixPanel('Act on Payee Addressbook Screen', {
        Action: 'Add New Payee from Search',
        Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      });
    } else {
      trackMixPanel('Act on Payee Addressbook Screen', {
        Action: 'Add New Payee',
        Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      });
    }
    setMoveMoneyData({
      ...moveMoneyData,
      vendorData: undefined,
      activeStepId: MoveMoneySteps.VENDOR_UPDATE,
      preFilledBusinessName: preFilledName || '',
      isEditModeFromAddressBook: true,
    });
  };

  const handleEditVendor = (e: React.MouseEvent<HTMLElement>, supplierData: ISupplierModel) => {
    e.stopPropagation();
    trackMixPanel('Act on Payee Addressbook Screen', {
      Action: 'Edit Payee',
      Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
    });
    setMoveMoneyData({
      ...moveMoneyData,
      vendorData: supplierData,
      activeStepId: MoveMoneySteps.VENDOR_UPDATE,
      isEditModeFromAddressBook: true,
    });
  };

  const handleVendorSelection = (supplierData: ISupplierModel) => {
    const isNameValid = isBusinessOrRecipientNameValid(supplierData.name || '');
    const isMainDataValid = isRequireDataForVendorValid(supplierData, moveType as MoveMoneyTypes);
    if (isMainDataValid && isNameValid) {
      setMoveMoneyData({
        ...moveMoneyData,
        vendorData: supplierData,
        activeStepId: moveMoneyData.isEditMode ? MoveMoneySteps.REVIEW : MoveMoneySteps.AMOUNT,
      });
    } else {
      trackMixPanel('Act on Payee Addressbook Screen', {
        Action: 'Select Payee',
        Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes),
      });
      setMoveMoneyData({
        ...moveMoneyData,
        vendorData: supplierData,
        activeStepId: MoveMoneySteps.VENDOR_UPDATE,
        isVendorUpdateMandatory: !isRequireDataForVendorValid(supplierData, moveType as MoveMoneyTypes),
        isEditMode: false,
        isUpdateNameRequired: !isNameValid,
      });
    }
  };

  useEffect(() => {
    trackMixPanel('View Payee Addressbook Screen', { Flow: getAnalyticsFlowName(moveType as MoveMoneyTypes) });
  }, []);

  const filteredSuppliers = moveMoneyData.supplierList.filter((supplier: ISupplierModel) =>
    supplier.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className='fixed overflow-auto left-0 top-0 w-full h-full bg-bw00 z-50 flex flex-col'>
      <NavigationHeader hideBackButton={moveMoneyData.isEditMode} onBack={handleBackButton} onClose={handleCloseFlow} />

      <Heading
        type='HTML'
        title={t(vendorPickerContentMap[moveType as keyof typeof vendorPickerContentMap].content)}
        className='mx-auto text-center mb-[12px] font-metricR text-bw08 text-[24px]'
      />
      <Heading
        title={t(vendorPickerContentMap[moveType as keyof typeof vendorPickerContentMap].sub_content)}
        className='mx-auto text-center mb-[22px] font-metricR text-bw07 text-[16px]'
      />
      <div className='mb-[12px] w-[395px] mx-auto'>
        <LiliSearchBar
          maxLength={40}
          value={filter}
          onChange={(filterValue) => setFilter(filterValue)}
          onClear={() => setFilter('')}
          placeHolder={t('LILI_SEARCH_BAR.PLACEHOLDER')}
        />
      </div>
      <AddNewVendor onAddNewVendor={() => handleAddNewVendor('')} />
      {(!!moveMoneyData.supplierList.length || filter) && (
        <Heading
          title={
            filteredSuppliers.length
              ? t(vendorPickerContentMap[moveType as keyof typeof vendorPickerContentMap].list_type)
              : t('MOVE_MONEY.STEPS.VENDOR_PICKER.EMPTY_LIST')
          }
          className='w-[395px] pb-[4px] border-b border-bw03 font-metricR text-bw07 text-[16px] mx-auto'
        />
      )}
      {filteredSuppliers.length > 0 && (
        <VendorsList
          vendorList={filteredSuppliers}
          onVendorSelection={handleVendorSelection}
          onEditVendor={handleEditVendor}
        />
      )}
      {!filteredSuppliers.length && filter && (
        <button onClick={() => handleAddNewVendor(filter)} type='button' className='w-fit mx-auto'>
          <div className='hover:bg-bw02 mt-[10px] active:bg-bw03 transition-all ease-in px-[10px] py-[10px] rounded-[8px] mb-[26px] w-[395px] flex items-center'>
            <AddVendorIcon />
            <Heading
              title={t(
                vendorPickerContentMap[moveType as keyof typeof vendorPickerContentMap].add_list_item_with_content,
                { filter },
              )}
              className='text-bw08 ml-[12px] text-[16px] font-metricRMedium max-w-[85%] truncate'
            />
            <RightArrowIcon className='ml-auto' />
          </div>
        </button>
      )}
    </div>
  );
};
